import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdArrowBack, MdCloudUpload, MdClose, MdPerson } from 'react-icons/md';
import axiosInstance from '../../api/axiosInstance';

const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

const TeamCreate = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    role: '',
    designation: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageError, setImageError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImageError('');

    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setImageError('Invalid file type. Only JPG, JPEG, PNG, and WEBP allowed.');
      e.target.value = '';
      return;
    }

    if (file.size > MAX_SIZE_BYTES) {
      setImageError('File too large. Maximum size is 5MB.');
      e.target.value = '';
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Name is required.');
      return;
    }

    setLoading(true);

    try {
      const data = new FormData();
      data.append('name', formData.name.trim());
      data.append('role', formData.role.trim());
      data.append('designation', formData.designation.trim());
      if (imageFile) data.append('image', imageFile);

      const response = await axiosInstance.post('/team', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        navigate('/admin/team');
      } else {
        setError(response.data.message || 'Failed to create team member.');
      }
    } catch (err) {
      setError(
        err.response?.data?.message || 'Something went wrong. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <button
        onClick={() => navigate('/admin/team')}
        className="flex items-center gap-2 text-gray-600 hover:text-[#008300] mb-4 sm:mb-6"
      >
        <MdArrowBack />
        <span>Back to Team</span>
      </button>

      <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 sm:mb-8">Add Team Member</h1>

      {error && (
        <div className="mb-4 sm:mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm sm:text-base">
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-100">
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Profile Photo <span className="text-gray-400 font-normal text-xs sm:text-sm">(JPG, PNG, WEBP — max 5MB)</span>
            </label>

            <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
              {/* Preview / Placeholder */}
              <div className="relative flex-shrink-0">
                {imagePreview ? (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover border-4 border-[#008300]/20 shadow"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 transition-colors"
                      title="Remove image"
                    >
                      <MdClose className="text-xs sm:text-sm" />
                    </button>
                  </div>
                ) : (
                  <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
                    <MdPerson className="text-3xl sm:text-4xl text-gray-400" />
                  </div>
                )}
              </div>

              {/* Upload Button */}
              <div className="flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={handleImageChange}
                  className="hidden"
                  id="team-image-upload"
                />
                <label
                  htmlFor="team-image-upload"
                  className="inline-flex items-center justify-center gap-2 cursor-pointer px-4 sm:px-5 py-2.5 border-2 border-[#008300] text-[#008300] rounded-lg font-medium hover:bg-[#008300] hover:text-white transition-colors text-sm sm:text-base"
                >
                  <MdCloudUpload className="text-lg sm:text-xl" />
                  Choose Photo
                </label>
                {imageFile && (
                  <p className="mt-2 text-xs sm:text-sm text-gray-500 truncate max-w-[150px] sm:max-w-xs">
                    {imageFile.name}
                  </p>
                )}
                {imageError && (
                  <p className="mt-2 text-xs sm:text-sm text-red-600">{imageError}</p>
                )}
              </div>
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g. Rahul Sharma"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008300] focus:border-transparent outline-none text-sm sm:text-base"
            />
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role
            </label>
            <input
              type="text"
              name="role"
              value={formData.role}
              onChange={handleChange}
              placeholder="e.g. Frontend Developer"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008300] focus:border-transparent outline-none text-sm sm:text-base"
            />
          </div>

          {/* Designation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Designation
            </label>
            <input
              type="text"
              name="designation"
              value={formData.designation}
              onChange={handleChange}
              placeholder="e.g. Senior Engineer"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008300] focus:border-transparent outline-none text-sm sm:text-base"
            />
          </div>

          <button
            type="submit"
            disabled={loading || !!imageError}
            className="w-full bg-[#008300] text-white py-3 rounded-lg font-semibold hover:bg-[#006600] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
          >
            {loading ? 'Adding Member...' : 'Add Team Member'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default TeamCreate;
